import * as React from 'react'
import { Box, Button, Card, Link as MuiLink, Table, Typography } from '@material-ui/core'
import Collapse from '@material-ui/core/Collapse'
import { FlexVertical, useToasts } from '@vestaboard/installables'
import { Alert } from '@material-ui/lab'
import { MarketplaceListingPropsEditor } from './MarketplaceListingPropsEditor'
import { INSTALLABLE_FORM_EMBED_URL } from '../../../env'

const STORYBOOK_PLAYGROUND_URL =
  'https://storybook2.vestaboard.com?path=%2Fstory%2Finstallableuiv2-renderer--playground'

/** Props schema for a single prop (same shape as InstallableDefinition.props[key]). */
type PropsSchema = Record<string, unknown>
/** Installable props: prop key → value. */
type InstallableProps = Record<string, unknown>

function isMessageFromEmbed(
  data: unknown
): data is { type: 'READY' } | { type: 'STATE_UPDATED'; payload: { values: InstallableProps } } {
  if (data == null || typeof data !== 'object') return false
  const obj = data as Record<string, unknown>
  if (!('type' in obj)) return false
  if (obj.type === 'READY') return true
  if (obj.type === 'STATE_UPDATED') {
    return typeof obj.payload === 'object' && obj.payload !== null && 'values' in (obj.payload as object)
  }
  return false
}

export interface V2InstallablePropsEditorProps {
  /** Installable definition JSON string (used for schema and copy). */
  installableDefinition: string | undefined
  /** Current V2 props JSON string. */
  value: string
  /** Called when props change (form or JSON editor). */
  onChange: (value: string) => void
  /** When true, show error state instead of form. */
  hasError?: boolean
}

function getPropsSchemas(installableDefinition: string | undefined): Record<string, PropsSchema> {
  if (!installableDefinition) return {}
  try {
    const def = JSON.parse(installableDefinition) as { props?: Record<string, PropsSchema> }
    return def?.props && typeof def.props === 'object' ? def.props : {}
  } catch {
    return {}
  }
}

export const V2InstallablePropsEditor: React.FC<V2InstallablePropsEditorProps> = ({
  installableDefinition,
  value,
  onChange,
  hasError = false
}) => {
  const { addToast } = useToasts()
  const [showJsonEditor, setShowJsonEditor] = React.useState(false)
  const [iframeHeight, setIframeHeight] = React.useState(200)
  const iframeRef = React.useRef<HTMLIFrameElement>(null)

  const embedUrl = INSTALLABLE_FORM_EMBED_URL
  const embedOrigin = React.useMemo(() => {
    if (!embedUrl) return ''
    try {
      return new URL(embedUrl).origin
    } catch {
      return ''
    }
  }, [embedUrl])
  const propsSchemas = React.useMemo(() => getPropsSchemas(installableDefinition), [installableDefinition])
  const hasSchema = Object.keys(propsSchemas).length > 0
  const showEmbed = Boolean(embedUrl && embedOrigin && hasSchema)

  const sendToIframe = React.useCallback(
    (message: { type: string; payload?: unknown }, targetOrigin: string) => {
      const win = iframeRef.current?.contentWindow
      if (!win) return
      try {
        win.postMessage(message, targetOrigin)
      } catch (e) {
        console.warn('V2 form iframe postMessage failed', e)
      }
    },
    []
  )

  const pushSchemaAndState = React.useCallback(
    (targetOrigin: string) => {
      let values: InstallableProps = {}
      try {
        if (value && value.trim()) values = JSON.parse(value) as InstallableProps
      } catch {
        /* use empty */
      }
      sendToIframe(
        {
          type: 'SET_STATE',
          payload: { values, targetOrigin: window.location.origin }
        },
        targetOrigin
      )
      sendToIframe(
        {
          type: 'SET_SCHEMA',
          payload: { schemas: propsSchemas, targetOrigin: window.location.origin }
        },
        targetOrigin
      )
    },
    [propsSchemas, value, sendToIframe]
  )

  React.useEffect(() => {
    if (!embedUrl || !embedOrigin) return
    const handler = (event: MessageEvent) => {
      if (event.origin !== embedOrigin) return
      const d = event.data as { type?: string; payload?: { height?: number; values?: InstallableProps } }
      if (d?.type === 'EMBED_HEIGHT' && typeof d?.payload?.height === 'number') {
        setIframeHeight(Math.max(200, d.payload.height))
        return
      }
      if (!isMessageFromEmbed(event.data)) return
      const msg = event.data
      if (msg.type === 'READY') {
        pushSchemaAndState(embedOrigin)
      } else if (msg.type === 'STATE_UPDATED' && msg.payload) {
        onChange(JSON.stringify(msg.payload.values, null, 2))
      }
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [embedUrl, embedOrigin, pushSchemaAndState, onChange])

  const handleIframeLoad = React.useCallback(() => {
    if (embedOrigin) pushSchemaAndState(embedOrigin)
  }, [embedOrigin, pushSchemaAndState])

  const handleCopyDefinition = React.useCallback(() => {
    if (!installableDefinition) return
    if (!navigator.clipboard?.writeText) {
      addToast('Clipboard access is not available in this browser.', { appearance: 'error' })
      return
    }
    navigator.clipboard
      .writeText(installableDefinition)
      .then(() => addToast('Installable definition copied.', { appearance: 'success' }))
      .catch(() => addToast('Unable to copy installable definition.', { appearance: 'error' }))
  }, [addToast, installableDefinition])

  const handleCopyProps = React.useCallback(() => {
    if (!value) return
    if (!navigator.clipboard?.writeText) {
      addToast('Clipboard access is not available in this browser.', { appearance: 'error' })
      return
    }
    navigator.clipboard
      .writeText(value)
      .then(() => addToast('Props JSON copied.', { appearance: 'success' }))
      .catch(() => addToast('Unable to copy props JSON.', { appearance: 'error' }))
  }, [addToast, value])

  if (hasError) {
    return (
      <Card style={{ marginTop: 30, maxWidth: 850 }}>
        <Alert severity='error'>There was an error loading the React Native props.</Alert>
      </Card>
    )
  }

  return (
    <>
      <FlexVertical spaceBetween>
        <Typography variant='h6'>Native (V2) Installable UI Properties (React Native)</Typography>
        <Typography variant='body2' color='textSecondary'>
          Configure the custom properties the V2 React Native renderer reads for this marketplace listing. These
          values are used only by the new native installable UI.
        </Typography>
        {showEmbed ? (
          <Typography variant='body2' color='textSecondary'>
            Use the form below to edit props. You can also edit the JSON directly in the collapsed section.
          </Typography>
        ) : (
          <>
            <Typography variant='body2' color='textSecondary'>
              Copy the installable definition (button below), then open the{' '}
              <MuiLink href={STORYBOOK_PLAYGROUND_URL} target='_blank' rel='noopener noreferrer'>
                Installable UI V2 playground
              </MuiLink>{' '}
              to configure props using the form. Copy the props JSON from the playground and paste it back here.
            </Typography>
            <Alert severity='info'>
              This is a temporary workflow. A dedicated form with validation for V2 props is coming soon.
            </Alert>
          </>
        )}
      </FlexVertical>
      <Card style={{ marginTop: 30, maxWidth: 850 }}>
        {showEmbed ? (
          <>
            <Box padding={1}>
              <iframe
                ref={iframeRef}
                src={embedUrl}
                title='V2 Installable Props Form'
                style={{
                  width: '100%',
                  minHeight: 200,
                  height: iframeHeight,
                  border: '1px solid #ccc',
                  borderRadius: 4,
                  display: 'block',
                  transition: 'height 0.25s ease-out'
                }}
                onLoad={handleIframeLoad}
              />
            </Box>
            <Box paddingLeft={1} paddingRight={1} paddingBottom={1}>
              <Button
                size='small'
                variant='text'
                onClick={() => setShowJsonEditor(prev => !prev)}
                style={{ textTransform: 'none' }}>
                {showJsonEditor ? 'Hide JSON' : 'Edit JSON directly'}
              </Button>
              <Collapse in={showJsonEditor}>
                <Table style={{ tableLayout: 'fixed', width: '100%' }}>
                  <MarketplaceListingPropsEditor value={value} onChange={onChange} />
                </Table>
              </Collapse>
            </Box>
          </>
        ) : (
          <Table style={{ tableLayout: 'fixed', width: '100%' }}>
            <MarketplaceListingPropsEditor value={value} onChange={onChange} />
          </Table>
        )}
      </Card>
      <Box marginTop={1} display='flex' alignItems='center' gridGap={8}>
        <Button size='small' variant='outlined' onClick={handleCopyDefinition} disabled={!installableDefinition}>
          Copy Installable Definition
        </Button>
        <Button size='small' variant='outlined' onClick={handleCopyProps} disabled={!value}>
          Copy Props JSON
        </Button>
        <MuiLink href={STORYBOOK_PLAYGROUND_URL} target='_blank' rel='noopener noreferrer'>
          Open Playground
        </MuiLink>
      </Box>
    </>
  )
}
